import React from 'react';
import { Popover, Transition } from '@headlessui/react';

interface InfoPopoverProps {
  content: React.ReactNode;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  content,
  title,
  children,
  className = '',
  iconClassName = '',
  placement = 'top'
}) => {

  const getPlacementClasses = () => {
    switch (placement) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  const getArrowClasses = () => {
    switch (placement) {
      case 'top':
        return 'top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-white dark:border-t-slate-800';
      case 'bottom':
        return 'bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-white dark:border-b-slate-800';
      case 'left':
        return 'left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-white dark:border-l-slate-800';
      case 'right':
        return 'right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-white dark:border-r-slate-800';
      default:
        return 'top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-white dark:border-t-slate-800';
    }
  };

  return (
    <Popover className={`relative inline-block ${className}`}>
      {({ open }) => (
        <>
          <Popover.Button
            className={`
              inline-flex items-center justify-center p-2 rounded-full
              text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300
              hover:bg-slate-100 dark:hover:bg-slate-800
              focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
              transition-colors duration-200 min-h-[44px] min-w-[44px]
              ${iconClassName}
            `}
            aria-label="Visa information"
          >
            {children || (
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            )}
          </Popover.Button>

          <Transition
            show={open}
            as={React.Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className={`
                absolute z-50 w-80 max-w-sm
                ${getPlacementClasses()}
              `}
            >
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-4">
                {/* Arrow */}
                <div className={`absolute w-0 h-0 border-4 ${getArrowClasses()}`} />
                
                {title && (
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    {title}
                  </h3>
                )}
                
                <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {content}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default InfoPopover;
